import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { t } from '../utils/i18n';

const CompanyNews = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getCompanyNews({
        page: pagination.current,
        page_size: pagination.pageSize,
        status: filterStatus,
      });
      if (res.code === 0) {
        setData(res.data?.list || []);
        setPagination(prev => ({ ...prev, total: res.data?.total || 0 }));
      }
    } catch (e) { message.error('获取列表失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [pagination.current, filterStatus]);

  const handleOpenCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleOpenEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await adminAPI.updateCompanyNews(editing.id, values);
        message.success('更新成功');
      } else {
        await adminAPI.createCompanyNews(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (e) { message.error('保存失败'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteCompanyNews(id);
      message.success('删除成功');
      fetchData();
    } catch (e) { message.error('删除失败'); }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '标题', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (s) => <Tag color={s === 'published' ? 'green' : 'default'}>{s === 'published' ? '已发布' : '草稿'}</Tag>,
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 170,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fit-screen-page">
      <div className="page-header">
        <h2 className="page-title">{t('company_news_manage')}</h2>
      </div>
      <div className="search-bar">
        <Select placeholder="状态筛选" allowClear style={{ width: 120 }} value={filterStatus || undefined}
          onChange={(v) => { setFilterStatus(v || ''); setPagination(prev => ({ ...prev, current: 1 })); }}>
          <Select.Option value="">全部</Select.Option>
          <Select.Option value="draft">草稿</Select.Option>
          <Select.Option value="published">已发布</Select.Option>
        </Select>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate} style={{ marginLeft: 12 }}>发布动态</Button>
      </div>
      <div className="table-container">
        <Table columns={columns} dataSource={data} rowKey="id" loading={loading}
          scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
          pagination={{ ...pagination, showSizeChanger: true, showTotal: t => `共 ${t} 条`,
            onChange: (p) => setPagination(prev => ({ ...prev, current: p })),
          }}
        />
      </div>

      <Modal title={editing ? '编辑动态' : '发布动态'} open={modalVisible}
        onOk={handleSave} onCancel={() => setModalVisible(false)} confirmLoading={saving} width={640}>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="请输入标题" />
          </Form.Item>
          <Form.Item name="summary" label="摘要">
            <Input.TextArea rows={2} placeholder="请输入摘要（可选）" />
          </Form.Item>
          <Form.Item name="cover_image" label="封面图URL">
            <Input placeholder="请输入封面图地址（可选）" />
          </Form.Item>
          <Form.Item name="content" label="正文">
            <Input.TextArea rows={6} placeholder="请输入正文内容（可选）" />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="draft">
            <Select>
              <Select.Option value="draft">草稿</Select.Option>
              <Select.Option value="published">发布</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyNews;
